import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, CalendarCheck, Droplets, Zap, TrendingUp, Trash2, X, Filter } from 'lucide-react';
import DatePicker from '../components/DatePicker';

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_IMG = '/plant-placeholder.svg';
const resolveImageUrl = (url) => {
  if (!url) return FALLBACK_IMG;
  if (url.startsWith('http') || url.startsWith('/plant-placeholder')) return url;
  return `${API}${url}`;
};

const CARE_OPTIONS = [
  { value: 'watering', label: 'Gießen', icon: Droplets },
  { value: 'fertilizing', label: 'Düngen', icon: Zap },
  { value: 'training', label: 'Trainieren', icon: TrendingUp },
  { value: 'topping', label: 'Topping', icon: CalendarCheck },
  { value: 'defoliation', label: 'Entblätterung', icon: CalendarCheck },
  { value: 'repotting', label: 'Umtopfen', icon: CalendarCheck },
  { value: 'other', label: 'Sonstiges', icon: CalendarCheck },
];
const CARE_LABELS = Object.fromEntries(CARE_OPTIONS.map(o => [o.value, o.label]));

export default function CareLogPage() {
  const [searchParams] = useSearchParams();
  const defaultProject = searchParams.get('project') || '';

  const [logs, setLogs] = useState([]);
  const [plants, setPlants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterProject, setFilterProject] = useState(defaultProject);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ plant_ids: [], care_type: 'watering', amount: '', notes: '', performed_at: '' });
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingImagePreviews, setPendingImagePreviews] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const logsUrl = filterProject ? `${API}/api/care-logs?project_id=${filterProject}` : `${API}/api/care-logs`;
      const [lRes, plRes, prRes] = await Promise.all([
        axios.get(logsUrl),
        axios.get(`${API}/api/plants`),
        axios.get(`${API}/api/projects`),
      ]);
      setLogs(Array.isArray(lRes.data) ? lRes.data : []);
      setPlants(Array.isArray(plRes.data) ? plRes.data : []);
      setProjects(Array.isArray(prRes.data) ? prRes.data : []);
    } catch { setLogs([]); setPlants([]); setProjects([]); }
    setLoading(false);
  }, [filterProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setForm({ plant_ids: [], care_type: 'watering', amount: '', notes: '', performed_at: '' });
    setPendingImages([]);
    setPendingImagePreviews([]);
    setError('');
    setShowModal(true);
  };

  const togglePlant = (id) => {
    setForm(f => ({
      ...f,
      plant_ids: f.plant_ids.includes(id) ? f.plant_ids.filter(p => p !== id) : [...f.plant_ids, id]
    }));
  };

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPendingImagePreviews(prev => [...prev, e.target.result]);
      setPendingImages(prev => [...prev, file]);
    };
    reader.readAsDataURL(file);
  };

  const removePendingImage = (index) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    setPendingImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.plant_ids.length === 0) { setError('Mindestens eine Pflanze auswählen'); return; }
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/care-logs`, form);
      if (pendingImages.length > 0 && data.id) {
        for (const file of pendingImages) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${API}/api/care-logs/${data.id}/images`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      const { data: fullLog } = await axios.get(`${API}/api/care-logs?plant_id=${data.plant_ids?.[0] || ''}`);
      setLogs(prev => {
        const filtered = prev.filter(l => l.id !== data.id);
        return [{ ...data, images: (fullLog || []).find(l => l.id === data.id)?.images || [] }, ...filtered];
      });
      setPendingImages([]);
      setPendingImagePreviews([]);
      setShowModal(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/care-logs/${id}`);
      setLogs(prev => prev.filter(l => l.id !== id));
    } catch {}
  };

  const getPlantName = (id) => plants.find(p => p.id === id)?.name || id;

  const getCareIcon = (type) => {
    const opt = CARE_OPTIONS.find(o => o.value === type);
    const Icon = opt?.icon || CalendarCheck;
    return <Icon size={16} />;
  };

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="care-log-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Pflegeprotokoll</h1>
          <p className="text-emerald-100/50 text-sm mt-0.5">{logs.length} Einträge</p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="create-care-log-btn">
          <Plus size={16} /> Eintrag
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter size={14} className="text-emerald-100/40" />
        <select className="input-grow w-auto text-sm" value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="filter-care-project">
          <option value="">Alle Projekte</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16" data-testid="no-care-logs">
          <CalendarCheck size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50 mb-4">Noch keine Pflegevorgänge</p>
          <button onClick={openCreate} className="btn-primary">Ersten Eintrag erstellen</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="care-log-list">
          {logs.map(log => (
            <div key={log.id} className="card-grow p-4" data-testid={`care-log-${log.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-emerald-400 flex-shrink-0"
                    style={{ background: 'rgba(16,185,129,0.1)' }}>
                    {getCareIcon(log.care_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-emerald-100">{CARE_LABELS[log.care_type] || log.care_type}</span>
                      {log.is_bulk && <span className="badge badge-blue">Bulk</span>}
                      {log.amount && <span className="text-xs text-emerald-100/50">{log.amount}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {log.plant_ids?.slice(0, 3).map(pid => (
                        <span key={pid} className="text-xs px-1.5 py-0.5 rounded text-emerald-100/60" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid #1B2A21' }}>
                          {getPlantName(pid)}
                        </span>
                      ))}
                      {log.plant_ids?.length > 3 && <span className="text-xs text-emerald-100/40">+{log.plant_ids.length - 3}</span>}
                    </div>
                    {log.notes && <p className="text-xs text-emerald-100/40 mt-1 truncate">{log.notes}</p>}
                    {(log.images || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        {log.images.map((url, i) => (
                          <img key={i} src={resolveImageUrl(url)} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-emerald-100/30 whitespace-nowrap">
                    {new Date(log.performed_at).toLocaleDateString('de-DE')}
                  </span>
                  <button onClick={() => handleDelete(log.id)} className="text-emerald-100/30 hover:text-red-400 transition-colors" data-testid={`delete-care-${log.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop" data-testid="care-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">Pflegevorgang eintragen</h2>
              <button onClick={() => setShowModal(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Pflegetyp</label>
                <div className="grid grid-cols-3 gap-2">
                  {CARE_OPTIONS.slice(0, 6).map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button key={opt.value} type="button"
                        onClick={() => setForm(f => ({ ...f, care_type: opt.value }))}
                        className={`flex items-center gap-1.5 p-2 rounded-lg text-xs font-medium transition-colors ${form.care_type === opt.value ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'text-emerald-100/50 hover:bg-[#050A07]'}`}
                        style={{ border: form.care_type === opt.value ? undefined : '1px solid #1B2A21' }}
                        data-testid={`care-type-${opt.value}`}>
                        <Icon size={12} />{opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Pflanzen auswählen *</label>
                <div className="max-h-36 overflow-y-auto space-y-1 p-2 rounded-lg" style={{ border: '1px solid #1B2A21', background: '#050A07' }}>
                  {plants.length === 0 ? (
                    <p className="text-xs text-emerald-100/30 text-center py-2">Keine Pflanzen vorhanden</p>
                  ) : plants.map(p => (
                    <label key={p.id} className="flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-[#111C15] transition-colors">
                      <input type="checkbox" checked={form.plant_ids.includes(p.id)} onChange={() => togglePlant(p.id)} className="accent-emerald-500" data-testid={`select-plant-${p.id}`} />
                      <span className="text-sm text-emerald-100">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Menge/Dosierung</label>
                <input className="input-grow" placeholder="z.B. 500ml, 2ml/L" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="care-amount-input" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Notizen</label>
                <textarea className="input-grow" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Zeitpunkt</label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <DatePicker
                      value={form.performed_at ? form.performed_at.slice(0, 10) : ''}
                      onChange={v => setForm(f => ({
                        ...f,
                        performed_at: v
                          ? `${v}T${f.performed_at && f.performed_at.length >= 16 ? f.performed_at.slice(11, 16) : '09:00'}`
                          : ''
                      }))}
                      placeholder="Datum wählen"
                      data-testid="care-date-input"
                    />
                  </div>
                  <input
                    type="time"
                    className="input-grow w-28"
                    value={form.performed_at && form.performed_at.length >= 16 ? form.performed_at.slice(11, 16) : ''}
                    onChange={e => setForm(f => ({
                      ...f,
                      performed_at: f.performed_at
                        ? `${f.performed_at.slice(0, 10)}T${e.target.value}`
                        : `${new Date().toISOString().slice(0, 10)}T${e.target.value}`
                    }))}
                    data-testid="care-time-input"
                  />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>Fotos (optional)</div>
                {pendingImagePreviews.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    {pendingImagePreviews.map((preview, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={preview} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }} />
                        <button
                          type="button"
                          onClick={() => removePendingImage(i)}
                          style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11 }}
                        >&#x2715;</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#4ade80' }}>
                    Foto aufnehmen
                    <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                      onChange={(e) => e.target.files[0] && handleImageSelect(e.target.files[0])} />
                  </label>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#60a5fa' }}>
                    Aus Galerie
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => e.target.files[0] && handleImageSelect(e.target.files[0])} />
                  </label>
                </div>
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-care-btn">
                  {saving ? 'Speichern...' : 'Eintragen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
